'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { reportError } from '@/hooks/use-error-reporter';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const { t } = useTranslation();

  // ChunkLoadError: force full reload so browser fetches fresh HTML with new chunk hashes
  useEffect(() => {
    if (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk')) {
      window.location.reload();
      return;
    }
    // Report other errors to Sentry/error-logs
    reportError(error, {
      component: 'app/error.tsx',
      severity: 'error',
      metadata: { digest: error.digest },
    });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center space-y-6 px-4">
        <div className="space-y-2">
          <AlertTriangle className="h-16 w-16 text-destructive mx-auto" />
          <h1 className="text-3xl font-bold text-foreground">
            {t('errors.page.serverError', '500')}
          </h1>
        </div>

        <div className="space-y-2">
          <p className="text-muted-foreground max-w-sm mx-auto">
            {t('errors.page.serverErrorMessage')}
          </p>
          {error.message && (
            <p className="text-xs text-muted-foreground/60">
              {error.message}
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={reset}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            {t('errors.page.serverErrorRetry')}
          </button>
          <Link
            href="/"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-input px-6 py-2.5 text-sm font-medium text-foreground shadow-sm hover:bg-accent transition-colors"
          >
            <Home className="h-4 w-4" />
            {t('errors.page.notFoundBackHome')}
          </Link>
        </div>

        {error.digest && (
          <p className="text-xs text-muted-foreground/40">
            Error ID: {error.digest}
          </p>
        )}
      </div>
    </div>
  );
}
